// src/components/AttachedFilesPreview.js - Fixed to properly display file metadata
import React from 'react';
import './FileAttachments.css';

/**
 * AttachedFilesPreview - Component for displaying currently attached files
 * in the horizontally scrollable list
 * FIXED: Added proper spacing between file size and type
 * 
 * @param {Object} props - Component props
 * @param {Array} props.files - Array of file objects
 * @param {Function} props.onRemoveFile - Function to remove a file
 * @param {Function} props.onToggleFileUpload - Function to toggle file upload area
 */
export default function AttachedFilesPreview({ files, onRemoveFile, onToggleFileUpload }) {
  if (!files || files.length === 0) {
    return null;
  }

  // Format file size for display
  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    
    return `${(bytes / Math.pow(1024, i)).toFixed(2)} ${sizes[i]}`;
  };

  // Get short file type display
  const getFileTypeDisplay = (fileType, fileName) => {
    // First check filename extension
    if (fileName) {
      const extension = fileName.split('.').pop()?.toLowerCase();
      if (extension) {
        return extension.toUpperCase();
      }
    }
    
    // If no extension in filename, check MIME type
    if (!fileType) return '';
    
    // Handle common MIME types
    if (fileType === 'application/pdf') return 'PDF';
    if (fileType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') return 'DOCX';
    if (fileType === 'application/msword') return 'DOC';
    if (fileType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') return 'XLSX';
    if (fileType === 'application/vnd.ms-excel') return 'XLS';
    if (fileType === 'text/csv') return 'CSV';
    
    // Default to the subtype part of the MIME type
    const typeParts = fileType.split('/');
    return typeParts.length > 1 ? typeParts[1].toUpperCase() : typeParts[0].toUpperCase();
  };

  // Get icon based on file type
  const getFileIcon = (fileType, fileName) => {
    if (fileType?.startsWith('image/') || fileName?.match(/\.(jpg|jpeg|png|gif|webp|svg|bmp)$/i)) {
      return '🖼️';
    }
    if (fileType === 'application/pdf' || fileName?.endsWith('.pdf')) {
      return '📄';
    }
    if (fileType?.includes('spreadsheet') || fileType?.includes('excel') || 
        fileType === 'text/csv' || fileName?.match(/\.(xlsx|xls|csv|ods)$/i)) {
      return '📊';
    }
    if (fileType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' || 
        fileName?.endsWith('.docx')) {
      return '📝';
    }
    if (fileType?.startsWith('text/') || 
        fileName?.match(/\.(txt|js|py|java|cpp|css|html|json|md)$/i)) {
      return '📝';
    }
    return '📎';
  };

  return (
    <div className="attached-files-preview">
      <div className="attached-files-header">
        <span>Attached Files ({files.length})</span>
        <button 
          className="toggle-upload-button"
          onClick={onToggleFileUpload}
        >
          Manage Files
        </button>
      </div>
      <div className="attached-files-list">
        {files.map((file, index) => (
          <div key={index} className="attached-file-item">
            <button
              className="remove-file-button"
              onClick={() => onRemoveFile(index)}
              aria-label={`Remove ${file.name || 'file'}`}
            >
              ×
            </button>
            <div className="file-icon">
              {getFileIcon(file.type, file.name)}
            </div>
            <div className="file-name" title={file.name}>
              {file.name || `File ${index + 1}`}
            </div>
            {/* FIXED: Added proper formatting with spacing between size and type */}
            <div className="file-meta">
              <span className="file-size">{formatFileSize(file.size)}</span>
              {/* Add a bullet separator between size and type */}
              <span className="file-meta-separator"> • </span>
              <span className="file-type-label">{getFileTypeDisplay(file.type, file.name)}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}